import BiMap from 'bidirectional-map';

import { assertIsDefined } from '@gitbook/util-ts';
import {
    normalizeURLSlugBasename,
    URL_SLUG_LIMIT,
    validateURLSlug,
    validateURLSlugBasename,
} from '@gitbook/util-url';

import {
    type Revision,
    type RevisionPage,
    type RevisionPageDocument,
    type RevisionPageGroup,
    type RevisionPageParent,
    RevisionPageType,
} from '../../types';
import { mutations } from '../../utils';
import { getPageAncestorsInRevision } from './getPageAncestorsInRevision';
import { getPageChildrenInRevision } from './getPageChildrenInRevision';
import { getPageParentInRevision } from './getPageParentInRevision';
import { getRootPagesInRevision } from './getRootPagesInRevision';
import { isRevisionPageLink, isRevisionPageParent } from './guards';

/**
 * Resolve a path slug in a parent (or root) level
 */
function resolvePathSlugInParent(
    revision: Revision,
    pathSection: string,
    parentId?: string
): RevisionPageParent | null;
function resolvePathSlugInParent(
    revision: mutations.MutableData<Revision>,
    pathSection: string,
    parentId?: string
): mutations.MutableData<RevisionPageParent> | null;
function resolvePathSlugInParent(
    revision: mutations.MutableData<Revision>,
    pathSection: string,
    parentId?: string
): mutations.MutableData<RevisionPageParent> | null {
    const pagePaths = getPagePathsInRevisionAt({ revision, parentId });
    let pageId = pagePaths.getKey(pathSection);
    // If the path ends with .html, try getting the page with a .html extension
    if (!pageId && pathSection.endsWith('.html')) {
        pageId = pagePaths.getKey(pathSection.replace(/\.html$/, ''));
    }

    // Still not found in revision
    if (!pageId) {
        return null;
    }

    const page = mutations.getIn(revision, ['pages', pageId] as const);
    if (page && !isRevisionPageParent(page)) {
        throw new Error('Path was associated with an incorrect page');
    }

    return page || null;
}

/**
 * Resolve a page path in the revision.
 */
export function resolvePathInRevision(revision: Revision, path: string): RevisionPageParent | null;
export function resolvePathInRevision(
    revision: Revision,
    path: string
): RevisionPageDocument | RevisionPageGroup | null;
export function resolvePathInRevision(
    revision: mutations.MutableData<Revision>,
    path: string
): mutations.MutableData<RevisionPageParent> | null;
export function resolvePathInRevision(
    revision: mutations.MutableData<Revision>,
    path: string
): mutations.MutableData<RevisionPageParent> | null {
    const parts = path.split('/').filter(Boolean);

    // Empty path defaults to first page
    if (parts.length === 0) {
        const firstPage = getRootPagesInRevision(revision).find(
            (p): p is mutations.MutableData<RevisionPageParent> =>
                mutations.get(p, 'type') !== RevisionPageType.Link
        );
        return firstPage || null;
    }

    const [first, ...rest] = parts;

    return rest.reduce(
        (result, part) => {
            if (!result) {
                return null;
            }

            return resolvePathSlugInParent(revision, part, mutations.get(result, 'id'));
        },
        resolvePathSlugInParent(revision, first)
    );
}

/**
 * Return a map from page id to page path for all pages in a revision.
 */
export function getPagePathsInRevision(
    revision: mutations.MutableData<Revision>,
    parentId?: string
): BiMap<string> {
    const paths = getPagePathsInRevisionAt({ revision, parentId });

    const currentEntries = Array.from(paths.entries());

    currentEntries.forEach(([pageId, path]) => {
        const descendantPaths = getPagePathsInRevision(revision, pageId);

        for (const entry of descendantPaths.entries()) {
            paths.set(entry[0], `${path}/${entry[1]}`);
        }
    });

    return paths;
}

/**
 * Return a map from page id to page path for only root pages in a revision.
 */
export function getRootPagesPathsInRevision(
    revision: mutations.MutableData<Revision>
): BiMap<string> {
    return getPagePathsInRevisionAt({ revision, parentId: undefined });
}

/**
 * Recomputing page paths can be expensive depending on the size and complexity of the Revision.
 * Although we already optimize by only computing paths for a single layer in the tree (determined
 * by the parent), we can optimize further by using a cache to prevent recalculations.
 */
const cachePath = mutations.memo<Revision, BiMap<string>>();

/**
 * Return a map from page id to page path at a level in the revision (not including descendants).
 */
function getPagePathsInRevisionAt({
    revision,
    parentId,
    newTitles = {},
}: {
    revision: mutations.MutableData<Revision>;
    parentId?: string;
    /** New titles to use when previewing changes */
    newTitles?: { [key: string]: string };
}): BiMap<string> {
    const getMap = () => {
        const result = new BiMap<string>();
        const pages = parentId
            ? getPageChildrenInRevision(revision, parentId)
            : getRootPagesInRevision(revision);

        const tryWithIndex = (basePath: string, index: number): string => {
            const suffix = `-${index}`;
            const newPath = `${basePath.slice(0, URL_SLUG_LIMIT - suffix.length)}${suffix}`;

            if (result.hasValue(newPath)) {
                return tryWithIndex(basePath, index + 1);
            }

            return newPath;
        };

        const allocatePath = (pageId: string, basePath: string) => {
            if (!result.hasValue(basePath)) {
                result.set(pageId, basePath);
                return basePath;
            }

            const withIndex = tryWithIndex(basePath, 1);
            result.set(pageId, withIndex);
            return withIndex;
        };

        pages.forEach((page) => {
            if (!isRevisionPageParent(page)) {
                return;
            }

            const pageId = mutations.get(page, 'id');
            const pageSlug = mutations.get(page, 'slug');
            // Normalize any invalid slugs before using them
            const validPageSlug =
                pageSlug && !validateURLSlugBasename(pageSlug)
                    ? normalizeURLSlugBasename(pageSlug)
                    : pageSlug;
            const pageTitle = mutations.get(page, 'title');
            const newTitle = newTitles[pageId];
            allocatePath(
                pageId,
                newTitle ? newTitle : validPageSlug || normalizeURLSlugBasename(pageTitle)
            );
        });

        return result;
    };

    // don't use the cache if we're using newTitles to preview changes
    const useCache = Object.keys(newTitles).length === 0;
    return useCache
        ? cachePath(revision, [mutations.get(revision, 'id'), parentId || ''], getMap)
        : getMap();
}

/**
 * Return the path of a page relative to its parent.
 *
 * @param newTitle new title to use to preview a potential new path (has to be normalized)
 */
export function getRelativePagePathInRevision({
    revision,
    pageId,
    newTitle,
}: {
    revision: mutations.MutableData<Revision>;
    pageId: string;
    newTitle?: string;
}): string {
    const page = mutations.getIn(revision, ['pages', pageId] as const);
    if (!page || isRevisionPageLink(page)) {
        throw new Error("Page doesn't exist or is a link");
    }

    const parent = getPageParentInRevision(revision, pageId);

    let newTitles = {};
    if (newTitle) {
        if (!validateURLSlug(newTitle)) {
            throw new Error(`expected normalized title, got: ${newTitle}`);
        }
        newTitles = { [pageId]: newTitle };
    }

    const pagePaths = getPagePathsInRevisionAt({
        revision,
        parentId: parent ? mutations.get(parent, 'id') : undefined,
        newTitles,
    });

    if (!pagePaths.has(pageId)) {
        const revisionId = mutations.get(revision, 'id');
        const contentId = mutations.get(revision, 'contentId');
        throw new Error(
            `path not allocated for page ID=${pageId} in "content/${contentId}/revisions/${revisionId}"`
        );
    }

    return pagePaths.get(pageId);
}

/**
 * Return the entire path for a page (concat of slugs of all parents)
 */
export function getAbsolutePagePathInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    handleRootPage = true
): string {
    // `/` resolves to the first page at the root
    if (handleRootPage) {
        const rootPages = mutations.get(revision, 'rootPages');
        if (mutations.get(rootPages, 0) === pageId) {
            return '';
        }
    }

    const ancestors = getPageAncestorsInRevision(revision, pageId);
    return ancestors
        .map((ancestor) => {
            if (!isRevisionPageParent(ancestor)) {
                throw new Error('Ancestor should always be a parent page');
            }

            return getRelativePagePathInRevision({
                revision,
                pageId: mutations.get(ancestor, 'id'),
            });
        })
        .join('/');
}

/**
 * Create a mapping of all page ids to their paths in a revision.
 */
export function getAbsolutePagePathsMap(
    revision: mutations.MutableData<Revision>,
    handleRootPage?: boolean
): Map<string, string> {
    const result = new Map<string, string>();

    mutations.values(mutations.get(revision, 'pages')).forEach((page) => {
        if (mutations.get(page, 'type') === RevisionPageType.Link) {
            return;
        }

        const pageId = mutations.get(page, 'id');
        const path = getAbsolutePagePathInRevision(revision, pageId, handleRootPage ?? false);
        result.set(pageId, path);
    });

    return result;
}

/**
 * List the previous paths of pages whose path changed or that were removed between two revisions,
 * with their new path (undefined when the page was removed). Only documents and groups have paths.
 */
export function getMovedPagePaths(
    previousRevision: Revision,
    revision: Revision
): Array<{ path: string; page: string; newPath: string | undefined }> {
    const previousPaths = getPagePathsInRevision(previousRevision);
    const paths = getPagePathsInRevision(revision);

    return Array.from(previousPaths.entries())
        .map(([page, path]) => ({ path, page, newPath: paths.get(page) }))
        .filter(({ path, newPath }) => newPath !== path);
}

/**
 * Resolve the first page document in a list of pages.
 */
export function resolveFirstDocument(
    revision: Revision,
    pages: RevisionPage[],
    ancestors: RevisionPageParent[]
): { page: RevisionPageDocument; ancestors: RevisionPageParent[] } | undefined {
    for (const page of pages) {
        if (page.type === 'link') {
            continue;
        }

        const result = resolvePageDocument(revision, page, ancestors);
        if (result) {
            return result;
        }
    }
}

function resolvePageDocument(
    revision: Revision,
    page: RevisionPage,
    ancestors: RevisionPageParent[]
): { page: RevisionPageDocument; ancestors: RevisionPageParent[] } | undefined {
    if (page.type === RevisionPageType.Group) {
        const groupPages = page.pages.map((pageId) => {
            const p = revision.pages[pageId];
            assertIsDefined(p);
            return p;
        });
        const firstDocument = resolveFirstDocument(revision, groupPages, [...ancestors, page]);
        return firstDocument;
    }
    if (page.type === RevisionPageType.Link || page.type === RevisionPageType.Computed) {
        return undefined;
    }

    return { page, ancestors };
}
